import { paths } from '@/api/openapi/schema';
import { CANVAS_SIZE, FONT_FAMILIES } from '@/constant';
import { SlideComponentDndCreator } from '@/providers/editor/creators/SlideComponentDnd.creator';
import { SlideComponentTextCreator } from '@/providers/editor/creators/SlideComponentText.creator';
import { SlideComponent, SlideComponentDnd, SlideComponentDndDragZone, SlideComponentDndDropZone, SlideComponentText } from '@/types/slide';

const TRADITIONAL_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZHKXinBiaoKai')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;
const SIMPLIFIED_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZKai-Z03S')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;

const INSTRUCTION_ZHHK = '請從選項中選擇最合適的詞語，填入句子的空格中，每個詞語只能用一次。';
const INSTRUCTION_ZHCN = '请从选项中选择最合适的词语，填入句子的空格中，每个词语只能用一次。';

export const generate = (input?: paths['/experiments/generators/questions/demo/fitb']['post']['responses']['200']['content']['application/json']): SlideComponent[][] => {
    if (!input || input.question?.type !== 'fill_in_the_blank' || !input.question.content) {
        console.log('Invalid input for fill in the blank question generation:', input);
        return [];
    }

    const slides: SlideComponent[][] = [];
    const components: SlideComponent[] = [];

    const margin = 16;

    /**
     * Create the instruction text component
     */
    const instruction: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
    instruction.content.text = input.question.content.language_variant === 'zh-Hant' ? INSTRUCTION_ZHHK : INSTRUCTION_ZHCN;
    instruction.pos = { x: margin, y: margin };
    instruction.size = { width: CANVAS_SIZE.width - margin * 2, height: 140 };
    instruction.content.typeSettings.fontFamily = input.question.content.language_variant === 'zh-Hant' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
    instruction.content.fontSize = 36;
    instruction.content.color = '#000000'; // Fallback to black if color is not defined

    /**
     * Create the question text component
     */
    const text: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
    text.content.text = input.question.content.question_sentence;
    text.pos = { x: margin, y: margin + instruction.size.height };
    text.size = { width: CANVAS_SIZE.width - margin * 2, height: 160 };
    text.content.typeSettings.fontFamily = input.question.content.language_variant === 'zh-Hant' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
    text.content.fontSize = 48;

    const width = 300;
    const height = 80;

    /**
     * Create the drag and drop components
     */
    const dnd: SlideComponentDnd = new SlideComponentDndCreator().create(crypto.randomUUID());

    const dropZone: SlideComponentDndDropZone = {
        id: crypto.randomUUID(),
        type: 'text',
        pos: { x: CANVAS_SIZE.width / 2 - width / 2, y: CANVAS_SIZE.height / 2 - height / 2 },
        size: { width, height },
        settings: { backgroundColor: '#DADEE7', multiple: true },
        tag: 'correct',
        connectIds: [],
        content: {
            text: '______',
            fontSize: 48,
            jyutping: [],
            color: '#000000',
            typeSettings: {
                fontFamily: input.question.content.language_variant === 'zh-Hant' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY,
                formatting: [],
                alignment: 'center',
                isPhonetic: false,
                borderRadius: 0,
                backgroundColor: 'transparent',
            },
        },
    };

    const dragZones: SlideComponentDndDragZone[] = [];

    for (const answer of input.question.content.answer_option) {
        const dragZone: SlideComponentDndDragZone = {
            id: crypto.randomUUID(),
            type: 'text',
            pos: { x: 0, y: CANVAS_SIZE.height - height - 40 - 16 },
            size: { width, height },
            settings: { backgroundColor: '#DADEE7' },
            tag: answer === input.question.content.correct_answer ? 'correct' : 'incorrect',
            content: {
                text: answer,
                fontSize: 48,
                jyutping: [],
                color: '#000000',
                typeSettings: {
                    fontFamily: input.question.content.language_variant === 'zh-Hant' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY,
                    formatting: [],
                    alignment: 'center',
                    isPhonetic: false,
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                },
            },
        };

        if (answer === input.question.content.correct_answer) {
            dropZone.connectIds.push(dragZone.id);
        }

        dragZones.push(dragZone);
    }

    const top2 = dragZones.slice(0, 2);
    const bottom2 = dragZones.slice(2, 4);

    // should always be a size of 4 (1 correct + 3 incorrect)
    top2.forEach((zone, i) => {
        // Assign a unique position for each drag zone
        zone.pos = { x: (1024 / top2.length) * (i + 1) - 1024 / top2.length / 2 - width / 2, y: zone.pos.y - height - 40 - 16 };
    });

    bottom2.forEach((zone, i) => {
        // Assign a unique position for each drag zone
        zone.pos = { x: (1024 / bottom2.length) * (i + 1) - 1024 / bottom2.length / 2 - width / 2, y: zone.pos.y };
    });

    dnd.content.dropZone = [dropZone];
    dnd.content.dragZone = dragZones;

    // make sure the dnd is below the text
    components.push(dnd);
    components.push(instruction);
    components.push(text);

    slides.push(components);

    return slides;
};
